import { IIconProps } from '@fluentui/react';
import { ErrorType } from '../../../models/HttpError';
import { AppState } from '../../../redux/reducers';
import { Staff } from '../../../models/Entities/Staff/Staff';
import { PaginationState } from '../../../models/PaginationState';

// store
import * as StaffStore from '../../../redux/middleware/Staff.Middleware';
import * as PersonsStore from '../../../redux/middleware/Persons.Middleware';

//#region Icons
export const AddIcon: IIconProps = { iconName: 'AddFriend' };
export const EditIcon: IIconProps = { iconName: 'Edit' };
export const ViewIcon: IIconProps = { iconName: 'ContactCard' };
export const RefreshIcon: IIconProps = { iconName: 'Refresh' };
//#endregion Icons

export interface IProps {}

interface ConnectedProps {
  isLoading: boolean | undefined;
  isLoadingSuccessfully: boolean | undefined;
  failOnLoading: boolean | undefined;
  isDeleting: boolean | undefined;
  failOnDeleting: boolean | undefined;
  isFixingIdentifiers: boolean | undefined;
  hasBrokenIdentifiers: boolean;
  error: ErrorType | undefined;
  staff: Staff[];
  pagination: PaginationState;
}

interface DispatchProps {
  GetAllStaff: typeof StaffStore.actionCreators.GetAllStaff;
  DeleteStaff: typeof StaffStore.actionCreators.DeleteStaff;
  FixPersonIdentifiers: typeof PersonsStore.actionCreators.FixPersonIdentifiers;
}

export type Props = IProps & ConnectedProps & DispatchProps;

/** Cuántos miembros se piden por página. */
export const DefaultPageSize = 20;

/**
 * Nombre visible de un miembro.
 *
 * La API de staff sólo publica la razón social de la persona. Para una persona
 * física eso es el nombre completo, porque es lo que el formulario arma al
 * guardar; cuando falta, el id es lo único cierto que se puede mostrar y es mejor
 * que una celda vacía, que no distingue "sin nombre" de "no cargó".
 */
export const GetStaffDisplayName = (staff: Staff): string =>
  staff.person?.businessName?.trim() || `#${staff.id}`;

const EmptyIdentifier = '00000000-0000-0000-0000-000000000000';

/** Un miembro cuya persona no tiene identificador único asignado. */
export const HasBrokenIdentifier = (staff: Staff): boolean =>
  !staff.person?.personIdentifier || staff.person.personIdentifier === EmptyIdentifier;

export const mapStateToProps = (state: AppState, ownProps: IProps) => ({
  isLoading: state.Staff?.isLoadingAll,
  isLoadingSuccessfully: state.Staff?.successLoadingAll,
  failOnLoading: state.Staff?.failOnLoadingAll,
  isDeleting: state.Staff?.isDeletingOne,
  failOnDeleting: state.Staff?.failOnDeletingOne,
  isFixingIdentifiers: state.Persons?.isFixingIdentifiers,
  // Sólo sobre la página que está a la vista. El listado viene cortado por el
  // servidor, así que no hay forma de saber desde acá si hay identificadores
  // rotos más adelante, y el aviso lo dice en esos términos en lugar de afirmar
  // algo sobre un conjunto que nunca vio.
  hasBrokenIdentifiers: (state.Staff?.list ?? []).some(HasBrokenIdentifier),
  error: state.Staff?.error,
  // Sin ordenar ni filtrar acá: el servidor devuelve una página ya buscada y ya
  // ordenada, y reordenar en el cliente sólo acomodaría los veinte que llegaron,
  // dando la impresión de un orden que el resto del listado no tiene.
  staff: state.Staff?.list ?? [],
  pagination: state.Staff?.pagination ?? {
    pageNumber: 1,
    pageSize: DefaultPageSize,
    totalPages: 0,
    totalRecords: 0
  },
  ...ownProps
});

export const mapDispatchToProps = {
  ...StaffStore.actionCreators,
  // La corrección de identificadores es de la API de personas y sigue siéndolo:
  // no es una operación sobre el staff, sino sobre las personas que hay detrás.
  FixPersonIdentifiers: PersonsStore.actionCreators.FixPersonIdentifiers
};
